import { useState, useEffect } from "react";
import api from "../lib/apiClient";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";

const ACTIVITIES = ["Électrotechnique", "IRVE — Bornes de recharge", "Location de matériel", "Plans & DAO", "Autre"];

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    activity: params.get("activity") === "Location" ? "Location de matériel" : "",
    subject: params.get("subject") || "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const subj = params.get("subject");
    const act = params.get("activity");
    if (subj) setForm((f) => ({ ...f, subject: subj }));
    if (act === "Location") setForm((f) => ({ ...f, activity: "Location de matériel" }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(true);
      if (data.email_sent) {
        toast.success("Message envoyé ! Nous vous recontactons rapidement.");
      } else {
        toast.success("Votre demande a bien été reçue.");
      }
      setForm({ name: "", email: "", phone: "", activity: "", subject: "", message: "" });
    } catch (e2) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Toaster position="top-right" />
      <section className="bg-gradient-to-br from-[#0F172A] via-[#1B3A6B] to-[#0F172A] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#FBBF24] mb-4">Contact</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight max-w-3xl">
            Parlons de votre projet
          </h1>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl">
            Devis gratuit et sans engagement, réponse sous 48h. Nous intervenons sur toute l'île de La Réunion.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1 space-y-6">
            <a href="tel:0692371331" className="block p-6 rounded-2xl border border-slate-200 hover:border-[#1B3A6B] transition-colors group" data-testid="contact-info-phone">
              <Phone className="w-6 h-6 text-[#FBBF24]" />
              <div className="mt-3 text-xs uppercase tracking-wider font-semibold text-slate-500">Téléphone</div>
              <div className="text-lg font-display font-bold text-slate-900 group-hover:text-[#1B3A6B]">0692 37 13 31</div>
            </a>
            <a href="mailto:contact@ingelec.re" className="block p-6 rounded-2xl border border-slate-200 hover:border-[#1B3A6B] transition-colors group" data-testid="contact-info-email">
              <Mail className="w-6 h-6 text-[#FBBF24]" />
              <div className="mt-3 text-xs uppercase tracking-wider font-semibold text-slate-500">Email</div>
              <div className="text-base font-display font-bold text-slate-900 group-hover:text-[#1B3A6B] break-all">contact@ingelec.re</div>
            </a>
            <div className="block p-6 rounded-2xl border border-slate-200" data-testid="contact-info-location">
              <MapPin className="w-6 h-6 text-[#FBBF24]" />
              <div className="mt-3 text-xs uppercase tracking-wider font-semibold text-slate-500">Adresse</div>
              <div className="text-base font-semibold text-slate-900">Sainte-Suzanne</div>
              <div className="text-sm text-slate-600">Île de La Réunion · 974</div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-7 lg:p-10" data-testid="contact-form">
              {sent ? (
                <div className="py-10 text-center" data-testid="contact-success">
                  <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                  <h3 className="mt-4 font-display text-2xl font-bold text-slate-900">Demande envoyée !</h3>
                  <p className="mt-2 text-slate-600">Nous vous recontactons sous 48h.</p>
                  <Button onClick={() => setSent(false)} className="mt-6 bg-[#1B3A6B] hover:bg-[#142c54] rounded-full" data-testid="contact-new-message">
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input id="name" data-testid="contact-input-name" value={form.name} onChange={set("name")} required className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} required className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input id="phone" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <Label htmlFor="city">Ville</Label>
                      <Input id="city" data-testid="contact-input-city" value={form.city} onChange={set("city")} placeholder="Sainte-Suzanne, Saint-Denis…" className="mt-1.5 h-11" />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="activity">Activité concernée</Label>
                      <Select value={form.activity} onValueChange={(v) => setForm((f) => ({ ...f, activity: v }))}>
                        <SelectTrigger id="activity" className="mt-1.5 h-11" data-testid="contact-input-activity">
                          <SelectValue placeholder="Sélectionner…" />
                        </SelectTrigger>
                        <SelectContent>
                          {ACTIVITIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="subject">Sujet *</Label>
                      <Input id="subject" data-testid="contact-input-subject" value={form.subject} onChange={set("subject")} required className="mt-1.5 h-11" />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={set("message")} required rows={6} className="mt-1.5" />
                    </div>
                  </div>
                  <Button type="submit" disabled={submitting} className="mt-6 bg-[#1B3A6B] hover:bg-[#142c54] rounded-full px-8 h-12" data-testid="contact-submit">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Envoyer ma demande
                  </Button>
                  <p className="mt-4 text-xs text-slate-500">
                    Les champs marqués d'une * sont obligatoires. En envoyant ce formulaire, vous acceptez d'être recontacté par INGélec.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
